import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Application from 'expo-application';
import * as Crypto from 'expo-crypto';
import { Platform } from 'react-native';
import { AGE_MAX_MS, collecteAppActive, DELAI_ENVOI_MS, FILE_MAX, LOT_MAX } from '../config/collecte';
import { useUserStore } from '../store/userStore';
import type { ChampsEvenementApp, EntreeFile, EvenementApp, NomEvenementApp, PlateformeApp } from '../types/pilotage';
import { capturerIncident } from './sentry';
import { supabase } from './supabase';

/**
 * Collecte du pilotage côté application. UN INVARIANT ENTRE TOUS : elle n'affecte jamais ce que la
 * personne voit ou fait. Aucune fonction d'ici ne lève, aucune n'est attendue par un écran, et un
 * échec ne se manifeste que par un incident Sentry (au plus un par lancement), jamais à l'écran.
 *
 * Les événements s'écrivent d'abord dans une file sur l'appareil (bornée en nombre et en âge), puis
 * partent par lots. Chaque événement reçoit son `event_id` à la note : un lot renvoyé après un échec
 * est sans effet côté base (idempotence). Aucun contenu libre n'entre ici : seuls des identifiants
 * qui respectent le motif de la base sont acceptés.
 */
export const CLE_FILE = 'pilotage_file_v1';

const MOTIF_IDENTIFIANT = /^[A-Za-z0-9_.:-]{1,120}$/;
const PLATEFORMES: readonly string[] = ['ios', 'android', 'web'];

let sessionId: string | null = null;
let dejaSignale = false;
let dejaVus = new Set<string>();
let envoiEnCours = false;
let minuteur: ReturnType<typeof setTimeout> | null = null;
let chaine: Promise<unknown> = Promise.resolve();

export function reinitialiserCollecte(): void {
  sessionId = null;
  dejaSignale = false;
  dejaVus = new Set<string>();
  envoiEnCours = false;
  if (minuteur) clearTimeout(minuteur);
  minuteur = null;
  chaine = Promise.resolve();
}

// Un seul incident par lancement : une panne persistante ne doit pas devenir un bruit par note.
function signaler(code: string): void {
  if (dejaSignale) return;
  dejaSignale = true;
  try {
    capturerIncident(code, 'config');
  } catch {
    // la télémétrie de la télémétrie ne doit rien casser
  }
}

// Toutes les lectures-modifications-écritures de la file passent par cette chaîne : deux notes
// rapprochées ne s'écrasent pas.
function enFile<T>(travail: () => Promise<T>): Promise<T> {
  const suite = chaine.then(travail, travail);
  chaine = suite.catch(() => undefined);
  return suite;
}

function versionApp(): string | null {
  // EXPO_PUBLIC_OPTIONNEL: sur le web il n'y a pas de binaire natif, la version ne peut venir que de l'export. Un repli
  // inventé (comme « inconnue ») fausserait les données par version, alors qu'une version absente est simplement omise.
  return Application.nativeApplicationVersion ?? process.env.EXPO_PUBLIC_APP_VERSION ?? null;
}

function identifiantDeSession(): string {
  if (!sessionId) sessionId = Crypto.randomUUID();
  return sessionId;
}

const ouAbsent = (v: string | undefined): string | undefined => (v !== undefined && MOTIF_IDENTIFIANT.test(v) ? v : undefined);

export function construireEvenement(
  nom: NomEvenementApp,
  champs: ChampsEvenementApp,
  c: { maintenant: Date; sessionId: string; platform: string; appVersion: string | null; nouvelId: () => string },
): EvenementApp | null {
  if (!PLATEFORMES.includes(c.platform)) return null;
  if (typeof champs.objetId !== 'string' || !MOTIF_IDENTIFIANT.test(champs.objetId)) return null;
  const objetType = ouAbsent(champs.objetType);
  const emplacement = ouAbsent(champs.emplacement);
  const seuil = ouAbsent(champs.seuilVersion);
  const appVersion = c.appVersion === null ? undefined : ouAbsent(c.appVersion);
  return {
    event_id: c.nouvelId(),
    event_name: nom,
    occurred_at: c.maintenant.toISOString(),
    session_id: c.sessionId,
    platform: c.platform as PlateformeApp,
    ...(appVersion ? { app_version: appVersion } : {}),
    ...(objetType ? { objet_type: objetType } : {}),
    objet_id: champs.objetId,
    ...(emplacement ? { emplacement } : {}),
    ...(seuil ? { seuil_lecture_version: seuil } : {}),
  } as EvenementApp;
}

export function elaguerFile(file: readonly EntreeFile[], maintenant: Date): EntreeFile[] {
  const recentes = file.filter((e) => {
    const t = Date.parse(e.evenement.occurred_at);
    return Number.isFinite(t) && maintenant.getTime() - t <= AGE_MAX_MS;
  });
  return recentes.slice(-FILE_MAX);
}

export function partagerFile(file: readonly EntreeFile[], uid: string): { lot: EntreeFile[]; reste: EntreeFile[] } {
  const lot: EntreeFile[] = [];
  const reste: EntreeFile[] = [];
  for (const e of file) {
    if (e.uid === uid && lot.length < LOT_MAX) lot.push(e);
    else reste.push(e);
  }
  return { lot, reste };
}

/** `data` seule ne suffit pas : une erreur transporte parfois une valeur. Garder = représenter plus tard. */
export function issueEnvoi(r: { data: unknown; error: unknown }): 'retirer' | 'garder' {
  if (r.error) return 'garder';
  return r.data === 'accepte' || r.data === 'ferme' || r.data === 'invalide' ? 'retirer' : 'garder';
}

function estEntree(e: unknown): e is EntreeFile {
  if (typeof e !== 'object' || e === null) return false;
  const { uid, evenement } = e as { uid?: unknown; evenement?: unknown };
  return (
    typeof uid === 'string' &&
    typeof evenement === 'object' &&
    evenement !== null &&
    typeof (evenement as { event_id?: unknown }).event_id === 'string' &&
    typeof (evenement as { occurred_at?: unknown }).occurred_at === 'string'
  );
}

async function lireFile(): Promise<EntreeFile[]> {
  const brut = await AsyncStorage.getItem(CLE_FILE);
  if (!brut) return [];
  try {
    const lue: unknown = JSON.parse(brut);
    return Array.isArray(lue) ? lue.filter(estEntree) : [];
  } catch {
    return [];
  }
}

async function ecrireFile(file: readonly EntreeFile[]): Promise<void> {
  if (file.length === 0) await AsyncStorage.removeItem(CLE_FILE);
  else await AsyncStorage.setItem(CLE_FILE, JSON.stringify(file));
}

function programmerEnvoi(): void {
  if (minuteur) return;
  minuteur = setTimeout(() => {
    minuteur = null;
    void envoyerFile();
  }, DELAI_ENVOI_MS);
}

export function noterEvenement(nom: NomEvenementApp, champs: ChampsEvenementApp, options?: { uneFoisParSession?: boolean }): void {
  try {
    if (!collecteAppActive()) return;
    const uid = useUserStore.getState().session?.user?.id;
    if (!uid) return;
    const evenement = construireEvenement(nom, champs, {
      maintenant: new Date(),
      sessionId: identifiantDeSession(),
      platform: Platform.OS,
      appVersion: versionApp(),
      nouvelId: () => Crypto.randomUUID(),
    });
    if (!evenement) return;
    if (options?.uneFoisParSession) {
      const cle = `${uid}|${nom}|${champs.objetId}`;
      if (dejaVus.has(cle)) return;
      dejaVus.add(cle);
    }
    enFile(async () => {
      const file = elaguerFile([...(await lireFile()), { uid, evenement }], new Date());
      await ecrireFile(file);
    }).then(programmerEnvoi, () => signaler('pilotage_app_ecriture_file'));
  } catch {
    signaler('pilotage_app_note_leve');
  }
}

export async function envoyerFile(): Promise<void> {
  if (envoiEnCours) {
    programmerEnvoi();
    return;
  }
  try {
    if (!collecteAppActive()) return;
    const uid = useUserStore.getState().session?.user?.id;
    if (!uid) return;
    envoiEnCours = true;
    for (;;) {
      const lot = await enFile(async () => {
        const lue = await lireFile();
        const elaguee = elaguerFile(lue, new Date()).filter((e) => e.uid === uid);
        if (elaguee.length !== lue.length) await ecrireFile(elaguee);
        return partagerFile(elaguee, uid).lot;
      });
      if (lot.length === 0) break;

      const declaredAt = new Date().toISOString();
      const reponse = await supabase.rpc('pilotage_enregistrer_evenements_app', {
        p_evenements: lot.map((e) => ({ ...e.evenement, declared_at: declaredAt })),
      });
      const code = reponse.error?.code;
      if (code) signaler(`pilotage_app_${code}`);
      else if (reponse.data === 'invalide') signaler('pilotage_app_invalide');
      if (issueEnvoi(reponse) === 'garder') break;

      const partis = new Set(lot.map((e) => e.evenement.event_id));
      await enFile(async () => ecrireFile((await lireFile()).filter((e) => !partis.has(e.evenement.event_id))));
    }
  } catch {
    signaler('pilotage_app_envoi_leve');
  } finally {
    envoiEnCours = false;
  }
}

export async function viderFileCollecte(): Promise<void> {
  try {
    if (minuteur) clearTimeout(minuteur);
    minuteur = null;
    dejaVus = new Set<string>();
    sessionId = null;
    await enFile(() => AsyncStorage.removeItem(CLE_FILE));
  } catch {
    // vider la file est un geste de confidentialité au mieux : il ne doit jamais retarder une déconnexion
  }
}
